import { useState, useEffect } from 'react';
import { PencilIcon, XIcon } from '@phosphor-icons/react';
import { ArrowLeftIcon, ArrowRightIcon, SignOutIcon } from '@phosphor-icons/react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Cookies from 'js-cookie';
import { useDispatch } from 'react-redux';
import { setRPack } from '../../../../../apps/frontend/src/redux/slices/packSlice';
import { setUser } from '../../../../../apps/frontend/src/redux/slices/userSlice';
import { Checkbox } from '../../components/ui/checkbox';
import LogoutModal from '../../components/modals/LogoutModal/LogoutModal';
import { OnboardingModal } from '../../Auth/AuthOnboarding/OnboardingModal';

const MyProfile = ({ onBack }) => {
  const navigate = useNavigate();
  const [isEditMode, setIsEditMode] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    class: '',
    board: '',
    parentEmail: '',
    profilePicture: '',
    examCategory: '',
    partialDropper: false,
  });
  const [tempParentEmail, setTempParentEmail] = useState('');
  const [sendReportToParent, setSendReportToParent] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const dispatch = useDispatch();
  const [showOnboardingModal, setshowOnboardingModal] = useState(false);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const cachedUser = JSON.parse(localStorage.getItem('userProfile'));
      if (cachedUser) {
        updateProfileState(cachedUser);
      }

      // In a real application, you might want to uncomment this to always fetch fresh data from the API
      const response = await axios.get('/api/auth/user');
      if (response.status === 200) {
        const user = response.data.data;
        localStorage.setItem('userProfile', JSON.stringify(user));
        updateProfileState(user);
      }
    } catch (error) {
      // Handle error, e.g., toast.error("Failed to fetch user data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, []); // Initial fetch when component mounts

  const updateProfileState = (user) => {
    setProfileData({
      name: user.name || '',
      email: user.email || '',
      phone: user.mobileNumber || '',
      address: `${user.state || ''}, ${user.country || ''}, ${user.pincode || ''}`,
      class: user.class?.title || '',
      board: user.board?.title === 'Others' ? user.boardName : user.board?.title || '',
      parentEmail: user.parentsEmail || '',
      profilePicture: user.profilePicture || '',
      examCategory: user.examCategory?.title || '',
      partialDropper: user.partialDropper || false,
    });
    setTempParentEmail(user.parentsEmail || '');
    setSendReportToParent(user.sendResultsToParents || false);
  };

  const handleSaveParentEmail = async () => {
    try {
      const response = await axios.patch('/api/user/profile/', {
        parentsEmail: tempParentEmail,
        sendResultsToParents: sendReportToParent,
      });

      if (response.status === 200) {
        const updatedUser = response.data.data.user;
        localStorage.setItem('userProfile', JSON.stringify(updatedUser));
        setProfileData((prev) => ({
          ...prev,
          parentEmail: tempParentEmail,
        }));
        toast.success("Parent's email updated successfully");
        setShowEmailModal(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Error updating Parent's email");
    }
  };

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    dispatch(setRPack(null));
    dispatch(setUser(null));
    localStorage.removeItem('userProfile');
    localStorage.removeItem('recentPack');
    localStorage.removeItem('redirect_url');
    sessionStorage.removeItem('token');
    Cookies.remove('token');
    window.webengage.user.logout();
    navigate('/auth');
  };

  const ProfileField = ({ label, value }) => (
    <div className="flex justify-between gap-4 py-3 items-start last:border-b-0">
      <span className="text-[var(--q3-neutral-light)] text-sm mt-0.1">{label}</span>
      <span className="text-[var(--q3-neutral-default)] text-sm font-medium text-right break-words max-w-full">
        {value || 'Not specified'}
      </span>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--q3-surface-default)] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[var(--q3-accent-normal)]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--q3-surface-default)]">
      {/* Mobile Header */}
      {onBack && (
        <div className="sticky top-0 z-50 bg-[var(--q3-surface-default)] border-b border-[var(--q3-stroke-normal)] md:hidden">
          <div className="flex items-center gap-3 p-4">
            <button onClick={onBack} className="p-1">
              <ArrowLeftIcon size={20} className="text-[var(--q3-neutral-default)]" />
            </button>
            <h1 className="text-lg font-semibold text-[var(--q3-neutral-default)]">My Profile</h1>
          </div>
        </div>
      )}

      <div className="w-full mx-auto px-4 py-6 md:px-0 md:py-10 md:pl-6">
        {/* Desktop Header */}
        <div className="hidden md:block mb-4 ml-6 -mt-8">
          <h1 className="text-2xl font-bold text-[var(--q3-neutral-default)]">My Profile</h1>
        </div>

        <div className="w-full mx-auto p-4 md:p-6 space-y-4">
          {/* 1️⃣ User Info Card */}
          <div className="bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-normal)] rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[var(--q3-surface-dimmer)] rounded-full overflow-hidden">
                  {profileData.profilePicture ? (
                    <img
                      src={profileData.profilePicture}
                      alt={profileData.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="w-full h-full flex items-center justify-center text-[var(--q3-neutral-default)] font-semibold text-lg">
                      {profileData.name.charAt(0)}
                    </span>
                  )}
                </div>
                <div>
                  <h2
                    className="text-lg font-semibold text-[var(--q3-neutral-default)] truncate max-w-[160px] sm:max-w-[200px] md:max-w-[400px]"
                    title={profileData.name}
                  >
                    {profileData.name}
                  </h2>

                  <p
                    className="text-sm text-[var(--q3-neutral-light)] truncate max-w-[170px] sm:max-w-[300px] md:max-w-[400px]"
                    title={profileData.email}
                  >
                    {profileData.email}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setshowOnboardingModal(true)}
                className="flex items-center gap-2 px-3 py-2 rounded-full border text-[var(--q3-neutral-default)] border-[var(--q3-stroke-normal)] bg-[var(--q3-surface-default)] hover:opacity-90 transition-opacity cursor-pointer sm: ml-2"
              >
                <PencilIcon size={16} />
                <span className="hidden md:inline text-sm font-medium">Edit Profile</span>
              </button>
            </div>
          </div>

          {/* 2️⃣ Profile Fields */}
          <div className="bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-normal)] rounded-lg p-6">
            <div className="space-y-1">
              <ProfileField label="Phone Number" value={profileData.phone?.replace(/^(\+91)(\d+)/, '$1 $2')} />
              <ProfileField label="Address" value={profileData.address} />
              <ProfileField label="Class" value={profileData.class} />
              <ProfileField label="Board" value={profileData.board} />
            </div>
          </div>

          {/* 3️⃣ Parent Email Section */}
          <div className="bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-normal)] rounded-lg p-6">
            <div className="flex justify-between items-center">
              <span className="text-[var(--q3-neutral-light)] text-sm">Parent's Email</span>
              <div className="flex items-center gap-2">
                <span className="text-[var(--q3-neutral-default)] text-sm font-medium">
                  {profileData.parentEmail || ''}
                </span>
                <button
                  onClick={() => {
                    setTempParentEmail(profileData.parentEmail);
                    setShowEmailModal(true);
                  }}
                  className={`flex items-center gap-1 transition-opacity cursor-pointer ${
                    profileData.parentEmail
                      ? 'text-[var(--q3-accent-normal)] text-sm underline hover:opacity-80'
                      : 'px-3 py-1 bg-[var(--q3-accent-normal)] text-[var(--q3-neutral-default_inverted)] text-xs rounded-md hover:opacity-90'
                  }`}
                >
                  {profileData.parentEmail ? (
                    'Edit'
                  ) : (
                    <>
                      Add Email <ArrowRightIcon size={14} />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          <div
            onClick={() => setShowLogoutModal(true)}
            className="group border border-[var(--q3-danger-light)] rounded-[12px] px-6 py-4 flex justify-center items-center cursor-pointer transition-colors duration-200 hover:!bg-[var(--q3-danger-light)]"
          >
            <div className="flex items-center gap-1 text-[var(--q3-danger-normal)] text-sm font-medium">
              Log Out
              <SignOutIcon
                size={16}
                className="transition-transform duration-200 transform group-hover:translate-x-1"
              />
            </div>
          </div>
        </div>
      </div>

      {/* ✉️ Parent Email Modal */}
      {showEmailModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{
            background: 'rgba(16, 24, 40, 0.70)', //used this colour as the given colour in figma was not visible on the background
            backdropFilter: 'blur(6px)',
          }}
        >
          <div className="relative bg-[var(--q3-surface-default)] rounded-lg p-6 w-full max-w-md mx-auto">
            <button
              onClick={() => setShowEmailModal(false)}
              className="absolute -top-10 left-1/2 -translate-x-1/2 border p-1 shadow-lg cursor-pointer"
              style={{
                borderRadius: '32px',
                border: '1px solid var(--overlayfab_default_stroke)',
                background: 'rgba(30, 30, 30, 0.85)', //used this colour as the given colour in figma was not visible on the background
              }}
            >
              <XIcon size={20} className="text-[var(--q3-surface-default)]" />
            </button>

            <h3 className="text-lg font-semibold text-[var(--q3-neutral-default)] mb-4">Parent's Email</h3>

            <div className="mb-4">
              <input
                type="email"
                placeholder="Enter Parent's Email Address"
                value={tempParentEmail}
                onChange={(e) => setTempParentEmail(e.target.value)}
                className="w-full px-3 py-2 border border-[var(--q3-stroke-normal)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--q3-accent-normal)] bg-[var(--q3-surface-default)] text-[var(--q3-neutral-default)] cursor-pointer"
              />
            </div>

            <div className="mb-6 p-3 rounded-[8px] bg-[var(--q3-surface-dimmer)] flex items-center gap-2">
              <Checkbox
                id="sendReport"
                checked={sendReportToParent}
                onCheckedChange={(checked) => setSendReportToParent(!!checked)}
                className="border-[var(--q3-stroke-normal)] cursor-pointer text-[var(--q3-accent-normal)]"
              />
              <label htmlFor="sendReport" className="text-sm text-[var(--q3-neutral-default)] cursor-pointer">
                Send test report on this email
              </label>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowEmailModal(false)}
                className="flex-1 px-4 py-2 rounded-full border border-[var(--q3-stroke-normal)] bg-[var(--q3-surface-default)] text-[var(--q3-neutral-default)] hover:bg-[var(--q3-surface-dimmer)] transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveParentEmail}
                disabled={!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(tempParentEmail)}
                className={`flex-1 px-4 py-2 rounded-full transition-opacity cursor-pointer ${
                  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(tempParentEmail)
                    ? 'bg-[var(--q3-accent-normal)] text-[var(--q3-neutral-default_inverted)] hover:opacity-90'
                    : 'bg-[var(--q3-accent-normal)] text-[var(--q3-neutral-default_inverted)] opacity-30 cursor-not-allowed'
                }`}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      <LogoutModal isOpen={showLogoutModal} onClose={() => setShowLogoutModal(false)} />
      <OnboardingModal
        open={showOnboardingModal}
        isProfile={true}
        onClose={() => setshowOnboardingModal(false)}
        onComplete={() => {
          setshowOnboardingModal(false);
          fetchUserData(); // Call fetchUserData when onboarding is complete
        }}
      />
    </div>
  );
};

export default MyProfile;
